import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  FILTER_KEYS,
  FILTER_PRIORITY_DISPLAY_THRESHOLD,
  type FilterProvider,
  isNestedFilterProvider,
  type SuppliedFilterProvider,
} from '@github-ui/filter-core'
import {
  ArchivedFilterProvider,
  AssigneeFilterProvider,
  AuthorFilterProvider,
  BaseFilterProvider,
  BlockedBySearchFilterProvider,
  BlockingSearchFilterProvider,
  ClosedFilterProvider,
  CommenterFilterProvider,
  CommentsFilterProvider,
  CreatedFilterProvider,
  DraftFilterProvider,
  HeadFilterProvider,
  InFilterProvider,
  InteractionsFilterProvider,
  InvolvesFilterProvider,
  IsFilterProvider,
  LabelFilterProvider,
  LanguageFilterProvider,
  LinkedFilterProvider,
  MentionsFilterProvider,
  MergedFilterProvider,
  MilestoneFilterProvider,
  OrgFilterProvider,
  ParentIssueFilterProvider,
  ProjectFilterProvider,
  ReactionsFilterProvider,
  ReasonFilterProvider,
  RelatesToSearchFilterProvider,
  RepositoryFilterProvider,
  ReviewedByFilterProvider,
  ReviewFilterProvider,
  ReviewRequestedFilterProvider,
  ShaFilterProvider,
  SortFilterProvider,
  StateFilterProvider,
  StatusFilterProvider,
  SuggestionsFilterProvider,
  TeamFilterProvider,
  TeamReviewRequestedFilterProvider,
  UpdatedFilterProvider,
  UserFilterProvider,
  UserReviewRequestedFilterProvider,
} from '@github-ui/filter-core/providers'
import {ISSUE_TYPE_FILTER_KEY, IssueTypeFilterProvider, type ProjectScope} from '@github-ui/issue-type-filter-provider'
import {useUser} from '@github-ui/use-user'
import {useEffect, useMemo, useState} from 'react'

import {IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES, IS_FILTER_PROVIDER_VALUES} from '../constants'
import {getIssueFieldFilterProvider} from './issue-field/issue-field'
import {getDashboardIssueFieldFilterProvider} from './issue-field/issue-field-dashboard'
import {SubIssueFilterProvider} from './sub-issue'

const conditionalProvider = (condition: boolean, provider: FilterProvider) => (condition ? [provider] : [])

type FilterProviderOverride = {
  legacy?: boolean
  projectScope?: ProjectScope
}

type AllowedOverrideKeys = (typeof ISSUE_TYPE_FILTER_KEY)['key']
type AllOverrides = Record<AllowedOverrideKeys, FilterProviderOverride>
type FilterProviderOverrides = Partial<AllOverrides>

export type FilterProviderContext = {
  repositoryScope?: string
  isOrgScope?: boolean
  issueTypesEnabled?: boolean
  isIssueFieldsEnabled?: boolean
  isIssueFieldsGlobalSearchEnabled?: boolean
  isIssueFieldsActive?: boolean
  // Set by issue-search consumers to opt into issue-only qualifiers (e.g. suggestions).
  isIssueSearchScope?: boolean
  /** Result of the `issues_relates_to_issues_search` flag; repo-scoped, so consumers evaluate it. */
  relatesToEnabled?: boolean
  providerOverrides?: FilterProviderOverrides
}

export const useFilterProviders = (context: FilterProviderContext) => {
  const {
    repositoryScope,
    isOrgScope = false,
    issueTypesEnabled = false,
    isIssueFieldsEnabled = false,
    isIssueFieldsGlobalSearchEnabled = false,
    isIssueFieldsActive = true,
    isIssueSearchScope = false,
    relatesToEnabled = false,
    providerOverrides = {},
  } = context

  const {currentUser} = useUser()

  const effectiveOverrides = isFeatureEnabled('memex_remove_deprecated_type_issue') ? providerOverrides : {}

  const isSuggestionsFilterEnabled = isIssueSearchScope && isFeatureEnabled('update_issue_suggestions')
  const isRelatesToFilterEnabled = isIssueSearchScope && relatesToEnabled

  const serializedOverrides = JSON.stringify(effectiveOverrides)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps use serialized key to compare by value, not object reference
  const stableOverrides = useMemo(() => effectiveOverrides, [serializedOverrides])

  const isRepoScope = !!repositoryScope
  const isFilterProviderValues = isRepoScope ? IS_FILTER_PROVIDER_VALUES : IS_FILTER_PROVIDER_NON_REPO_SCOPE_VALUES

  const [asyncProviders, setAsyncProviders] = useState<SuppliedFilterProvider[]>([])

  const providers: SuppliedFilterProvider[] = useMemo(() => {
    const userFilterParam = {
      showAtMe: !!currentUser,
      currentUserLogin: currentUser?.login,
      currentUserAvatarUrl: currentUser?.avatarUrl,
      repositoryScope,
    }

    const noNoneValueFilterConfig = {filterTypes: {valueless: false}}
    const hasValueFilterConfig = {filterTypes: {hasValue: true}}

    const getOverride = (key: AllowedOverrideKeys) => stableOverrides[key] ?? {}

    const allProviders = [
      ...conditionalProvider(
        !isRepoScope,
        new RepositoryFilterProvider({...noNoneValueFilterConfig, filterTypes: {multiKey: true}}),
      ),
      ...conditionalProvider(!isRepoScope, new OrgFilterProvider({...noNoneValueFilterConfig})),
      new IsFilterProvider(isFilterProviderValues, noNoneValueFilterConfig),
      new StateFilterProvider('mixed', noNoneValueFilterConfig),
      new LabelFilterProvider(hasValueFilterConfig),
      ...conditionalProvider(
        issueTypesEnabled || isOrgScope || !isRepoScope,
        new IssueTypeFilterProvider(
          hasValueFilterConfig,
          // legacy support for `type:issue` and `type:pr`
          getOverride(ISSUE_TYPE_FILTER_KEY.key).legacy ?? true,
          repositoryScope,
          getOverride(ISSUE_TYPE_FILTER_KEY.key).projectScope,
        ),
      ),
      ...asyncProviders,
      new ProjectFilterProvider(hasValueFilterConfig),
      new MilestoneFilterProvider(hasValueFilterConfig),
      new AssigneeFilterProvider({...userFilterParam, showAtCopilot: true}, hasValueFilterConfig),
      new AuthorFilterProvider({...userFilterParam, showAtCopilot: true}, noNoneValueFilterConfig),
      new InvolvesFilterProvider({...userFilterParam, showAtCopilot: true}, noNoneValueFilterConfig),
      new MentionsFilterProvider(userFilterParam, noNoneValueFilterConfig),
      new ParentIssueFilterProvider(FILTER_KEYS.parentIssue, hasValueFilterConfig),
      new BlockingSearchFilterProvider(),
      new BlockedBySearchFilterProvider(),
      ...conditionalProvider(isRelatesToFilterEnabled, new RelatesToSearchFilterProvider()),
      new SubIssueFilterProvider(),
      new UpdatedFilterProvider(noNoneValueFilterConfig),
      new CreatedFilterProvider(noNoneValueFilterConfig),
      new ClosedFilterProvider(noNoneValueFilterConfig),
      new MergedFilterProvider(noNoneValueFilterConfig),
      new ReviewRequestedFilterProvider({...userFilterParam, showAtCopilot: true}, noNoneValueFilterConfig),
      new InFilterProvider(),
      new CommenterFilterProvider(userFilterParam, noNoneValueFilterConfig),
      new UserFilterProvider(userFilterParam, noNoneValueFilterConfig),
      new UserReviewRequestedFilterProvider(userFilterParam, noNoneValueFilterConfig),
      new ReviewedByFilterProvider({...userFilterParam, showAtCopilot: true}, noNoneValueFilterConfig),
      new CommentsFilterProvider(noNoneValueFilterConfig),
      new InteractionsFilterProvider(noNoneValueFilterConfig),
      ...conditionalProvider(isSuggestionsFilterEnabled, new SuggestionsFilterProvider()),
      new ReasonFilterProvider(noNoneValueFilterConfig),
      new LinkedFilterProvider(['issue', 'pr'], noNoneValueFilterConfig),
      new ArchivedFilterProvider(noNoneValueFilterConfig),
      new ReactionsFilterProvider(noNoneValueFilterConfig),
      new DraftFilterProvider(noNoneValueFilterConfig),
      new ReviewFilterProvider(noNoneValueFilterConfig),
      new LanguageFilterProvider(noNoneValueFilterConfig),
      new ShaFilterProvider(noNoneValueFilterConfig),
      new BaseFilterProvider(noNoneValueFilterConfig),
      new HeadFilterProvider(noNoneValueFilterConfig),
      new StatusFilterProvider(noNoneValueFilterConfig),
      new TeamFilterProvider(repositoryScope, noNoneValueFilterConfig),
      new TeamReviewRequestedFilterProvider(repositoryScope, noNoneValueFilterConfig),
      new SortFilterProvider(['created', 'updated', 'reactions', 'comments', 'relevance'], noNoneValueFilterConfig),
    ]

    // Normalize provider priorities to ensure they are shown in suggestions.
    // Providers with priority > FILTER_PRIORITY_DISPLAY_THRESHOLD won't appear in suggestions,
    // so we cap them at the threshold while preserving relative ordering,
    // and while preserving Priority order of other providers.
    for (const provider of allProviders) {
      if (isNestedFilterProvider(provider)) {
        // For NestedFilterProvider, update the priority of all child filterProviders
        for (const childProvider of provider.filterProviders) {
          if ('priority' in childProvider && childProvider.priority > FILTER_PRIORITY_DISPLAY_THRESHOLD) {
            childProvider.priority = FILTER_PRIORITY_DISPLAY_THRESHOLD
          }
        }
      } else if ('priority' in provider && provider.priority > FILTER_PRIORITY_DISPLAY_THRESHOLD) {
        provider.priority = FILTER_PRIORITY_DISPLAY_THRESHOLD
      }
    }

    return allProviders
  }, [
    currentUser,
    repositoryScope,
    isRepoScope,
    asyncProviders,
    isFilterProviderValues,
    isOrgScope,
    issueTypesEnabled,
    stableOverrides,
    isSuggestionsFilterEnabled,
    isRelatesToFilterEnabled,
  ])

  useEffect(() => {
    // Fetch the issue field provider asynchronously
    const fetchIssueFieldProvider = async () => {
      let issueFieldProvider

      if (repositoryScope && isIssueFieldsEnabled) {
        // Repository scope - use repository-specific provider
        issueFieldProvider = await getIssueFieldFilterProvider(
          {filterTypes: {hasValue: true, valueless: true}},
          repositoryScope,
        )
      } else if (isIssueFieldsGlobalSearchEnabled) {
        // Global search - use dashboard provider
        issueFieldProvider = await getDashboardIssueFieldFilterProvider({
          filterTypes: {hasValue: true, valueless: true},
        })
      }

      if (issueFieldProvider) {
        setAsyncProviders(prev => {
          const filtered = prev.filter(provider => {
            return !(
              ('key' in provider && provider.key === 'field') ||
              ('filterProviders' in provider && provider.filterProviders?.some(fp => fp.key?.startsWith('field.')))
            )
          })
          return [...filtered, issueFieldProvider]
        })
      }
    }

    if (isIssueFieldsActive && ((repositoryScope && isIssueFieldsEnabled) || isIssueFieldsGlobalSearchEnabled)) {
      fetchIssueFieldProvider()
    }
  }, [isIssueFieldsActive, isIssueFieldsEnabled, isIssueFieldsGlobalSearchEnabled, repositoryScope])

  return providers
}
